import { z } from 'zod';
import { Cents, IsoDateTime, Uuid } from './common.js';
import { R1Key } from './r1.js';

/**
 * Lot AT « Devis après le R1, créé dans Axonaut » (décisions de Maxime du 17 et du 18/10/2026).
 *
 * - Le catalogue des produits Axonaut est relu par la synchro Axonaut (`GET /products`, lecture seule).
 * - Le brouillon de devis VIT DANS LE COCKPIT (`quote_drafts`) : notes collées (gardées), lignes, doutes,
 *   commentaire, bloc client. Rien n'existe dans Axonaut avant la validation du plan `quote.create`
 *   (Axonaut n'a pas de brouillon : le devis est numéroté dès sa création).
 * - Un brouillon par R1 (`r1Key`), sinon par client (carte « Envoyer le devis » sans R1).
 */

export const QuoteDraftStatus = z.enum(['DRAFT', 'CREATED', 'DISMISSED']);
export type QuoteDraftStatus = z.infer<typeof QuoteDraftStatus>;

/** Taux de TVA de l'agence (repli d'une ligne sans taux). */
export const DEFAULT_QUOTE_TAX_RATE = 20;
/** Lignes d'un brouillon au plus. */
export const QUOTE_DRAFT_MAX_LINES = 40;
/** Longueur des notes collées au plus (une transcription d'une heure tient largement). */
export const QUOTE_NOTES_MAX = 60_000;

// ------------------------------------------------------------------ Catalogue

/** Un produit du catalogue Axonaut (table `axonaut_products`, relue par la synchro Axonaut). */
export const CatalogProduct = z.object({
  /** Identifiant Axonaut du produit (`products[].id` de la ligne de devis). */
  id: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  description: z.string().nullable(),
  /** Prix unitaire HT, en centimes. */
  priceCents: Cents,
  /** Taux de TVA en pourcentage (20, 10, 5,5…). */
  taxRate: z.number(),
  unit: z.string().nullable(),
  category: z.string().nullable(),
  /** Désactivé dans Axonaut (ou disparu du catalogue) : plus proposé dans la recherche. */
  disabled: z.boolean(),
});
export type CatalogProduct = z.infer<typeof CatalogProduct>;

/** `GET /api/quote-drafts/catalog` */
export const QuoteCatalog = z.object({
  items: z.array(CatalogProduct),
  /** Dernière lecture du catalogue par la synchro Axonaut (null : jamais lu). */
  syncedAt: IsoDateTime.nullable(),
});
export type QuoteCatalog = z.infer<typeof QuoteCatalog>;

// ------------------------------------------------------------------ Lignes

const text = (max: number) => z.string().max(max);
const optText = (max: number) => z.string().max(max).nullable();

/**
 * Une ligne du brouillon. `productId` = produit du catalogue Axonaut ; null = prestation HORS CATALOGUE : à la
 * validation, le plan crée le produit dans Axonaut (étape visible et décochable), sauf si l'on prend « le produit
 * le plus proche » (`closestProductId`), qui devient alors `productId` en gardant le titre, la description et le
 * prix de la ligne.
 */
export const QuoteDraftLine = z.object({
  /** Clé stable de la ligne dans le brouillon (étapes du plan, focus de l'écran). */
  key: z.string().min(1).max(40),
  productId: z.string().max(40).nullable(),
  /** Nom et code du produit du catalogue au moment du choix (« catalogue Axonaut · SITE-VIT-6 »). */
  productName: optText(300),
  productCode: optText(100),
  /** Produit du catalogue le plus proche, proposé pour une ligne hors catalogue. */
  closestProductId: z.string().max(40).nullable(),
  /** Ligne hors catalogue : « Créer le produit dans Axonaut » confirmé (sinon encore à trancher). */
  newProductConfirmed: z.boolean(),
  /** Titre de la ligne sur le devis (modifiable, même pour un produit du catalogue). */
  name: z.string().trim().min(1).max(300),
  description: optText(4000),
  quantity: z.number().positive().max(100_000),
  unitPriceCents: z.int().min(0).max(100_000_000),
  /** Remise en pourcentage (100 = offert). */
  discountPercent: z.number().min(0).max(100),
  taxRate: z.number().min(0).max(100),
  unit: optText(40),
  /** « Pourquoi » : la phrase des notes qui justifie la ligne (citée entre guillemets). */
  reason: optText(1000),
  /** « À vérifier » : ce dont Claude n'est pas sûr. */
  doubt: optText(1000),
  addedBy: z.enum(['claude', 'user']),
  /**
   * Lot AY : ligne sous-traitée (sous-traitant = fournisseur Axonaut `suppliers.id`, coût payé HT, prérempli avec le
   * total de la ligne). Recopiée sur le devis Axonaut créé (sous-traitance du devis, « Préparer le projet » prérempli).
   * Absent ou null : faite en interne ; `supplierId` null : sous-traitant pas encore choisi (rien n'est recopié).
   */
  subcontract: z
    .object({ supplierId: Uuid.nullable(), costCents: z.int().min(0).max(100_000_000) })
    .nullable()
    .optional(),
});
export type QuoteDraftLine = z.infer<typeof QuoteDraftLine>;

// ------------------------------------------------------------------ Client

/** D'où vient la valeur d'un champ du bloc client (affichée en vert sous le libellé). */
export const QuoteFieldSource = z.enum(['registry', 'r1', 'agenda', 'notes', 'axonaut', 'claude']);
export type QuoteFieldSource = z.infer<typeof QuoteFieldSource>;
export const QUOTE_FIELD_SOURCE_LABELS: Record<QuoteFieldSource, string> = {
  registry: 'registre public',
  r1: 'infos du R1',
  agenda: 'invitation de l’agenda',
  notes: 'tes notes',
  axonaut: 'Axonaut',
  claude: 'proposé par Claude',
};

export const QuoteDraftCompany = z.object({
  name: optText(200),
  /** SIRET du siège (Axonaut n'a pas de champ SIREN). */
  siret: optText(20),
  street: optText(300),
  zipCode: optText(20),
  city: optText(120),
  country: optText(80),
  isProspect: z.boolean(),
});
export type QuoteDraftCompany = z.infer<typeof QuoteDraftCompany>;

export const QuoteDraftContact = z.object({
  firstname: optText(100),
  lastname: optText(100),
  role: optText(120),
  email: optText(320),
  phone: optText(40),
});
export type QuoteDraftContact = z.infer<typeof QuoteDraftContact>;

export const QuoteDraftClient = z.object({
  /** `existing` : fiche Axonaut choisie (rien à créer) ; `new` : créée à la validation ; null : pas encore cherché. */
  mode: z.enum(['existing', 'new']).nullable(),
  axonautCompanyId: z.string().max(40).nullable(),
  clientId: Uuid.nullable(),
  /** Comment la fiche existante a été retenue. */
  foundBy: z.enum(['siret', 'name', 'r1', 'chosen']).nullable(),
  company: QuoteDraftCompany,
  contact: QuoteDraftContact,
  /** Source de chaque champ pré-rempli (`company.name`, `contact.email`…), tant qu'il n'a pas été modifié. */
  sources: z.record(z.string(), QuoteFieldSource),
  /** Fiches Axonaut écartées par « Non, créer un nouveau client » (doublons possibles). */
  dismissedMatches: z.array(z.string().max(40)),
});
export type QuoteDraftClient = z.infer<typeof QuoteDraftClient>;

// ------------------------------------------------------------------ Brouillon

export const QuoteDraftAxonaut = z.object({
  quoteId: z.string(),
  number: z.string().nullable(),
  /** Lien du devis dans Axonaut (`public_path`), sinon la fiche du client. */
  url: z.string().nullable(),
  companyId: z.string().nullable(),
  createdAt: IsoDateTime,
  createdBy: Uuid.nullable(),
});
export type QuoteDraftAxonaut = z.infer<typeof QuoteDraftAxonaut>;

export const QuoteDraftR1 = z.object({
  title: z.string(),
  startAt: IsoDateTime,
  members: z.array(z.object({ id: Uuid, name: z.string() })),
});

export const QuoteDraft = z.object({
  id: Uuid,
  r1Key: R1Key.nullable(),
  /** Le prospect (« Boulangerie Lemoine »), repris dans le titre « Devis de … ». */
  prospect: z.string(),
  status: QuoteDraftStatus,
  notes: z.string(),
  notesBy: Uuid.nullable(),
  notesAt: IsoDateTime.nullable(),
  /** Résumé de la proposition de Claude (une ou deux phrases). */
  summary: z.string().nullable(),
  lines: z.array(QuoteDraftLine),
  doubts: z.array(z.string()),
  /** Infos à demander au prospect (relevées par Claude). */
  missing: z.array(z.string()),
  /** Commentaire du devis (repris dans Axonaut). */
  comment: z.string(),
  client: QuoteDraftClient,
  proposedAt: IsoDateTime.nullable(),
  proposedBy: Uuid.nullable(),
  /** Coût cumulé des propositions de Claude (centimes de dollar). */
  costCents: Cents.nullable(),
  axonaut: QuoteDraftAxonaut.nullable(),
  dismissedAt: IsoDateTime.nullable(),
  createdBy: Uuid.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime.nullable(),
  updatedBy: Uuid.nullable(),
  /** Le R1 du brouillon (en-tête), null sans R1 ou s'il a disparu de l'agenda. */
  r1: QuoteDraftR1.nullable(),
});
export type QuoteDraft = z.infer<typeof QuoteDraft>;

/** Résumé d'un brouillon (bloc « Après le R1 », pastille « Brouillon en cours » des cartes). */
export const QuoteDraftSummary = z.object({
  id: Uuid,
  r1Key: R1Key.nullable(),
  clientId: Uuid.nullable(),
  prospect: z.string(),
  status: QuoteDraftStatus,
  lineCount: z.int(),
  totalCents: Cents,
  updatedAt: IsoDateTime,
  updatedBy: Uuid.nullable(),
  axonautNumber: z.string().nullable(),
  axonautUrl: z.string().nullable(),
});
export type QuoteDraftSummary = z.infer<typeof QuoteDraftSummary>;

export const QuoteDraftSummaryList = z.array(QuoteDraftSummary);

/** `GET /api/quote-drafts?r1Key=&status=` */
export const QuoteDraftListQuery = z.object({
  r1Key: R1Key.optional(),
  status: z.enum(['open', 'all']).default('open'),
});
export type QuoteDraftListQuery = z.input<typeof QuoteDraftListQuery>;

/** `POST /api/quote-drafts` : ouvre le brouillon d'un R1 ou d'une carte (le crée au besoin). */
export const OpenQuoteDraftBody = z
  .object({ r1Key: R1Key.optional(), actionId: Uuid.optional() })
  .refine((b) => b.r1Key !== undefined || b.actionId !== undefined, {
    message: 'Un R1 ou une carte est attendu',
  });
export type OpenQuoteDraftBody = z.infer<typeof OpenQuoteDraftBody>;

/** `PATCH /api/quote-drafts/:id` : enregistrement au fil de l'eau (seuls les champs envoyés changent). */
export const UpdateQuoteDraftBody = z
  .object({
    notes: text(QUOTE_NOTES_MAX),
    lines: z.array(QuoteDraftLine).max(QUOTE_DRAFT_MAX_LINES),
    doubts: z.array(text(1000)).max(30),
    missing: z.array(text(500)).max(30),
    comment: text(4000),
    client: QuoteDraftClient,
  })
  .partial();
export type UpdateQuoteDraftBody = z.infer<typeof UpdateQuoteDraftBody>;

// ------------------------------------------------------------------ Recherche du client

/** Une fiche Axonaut qui correspond (ou ressemble) au prospect. */
export const QuoteClientMatch = z.object({
  axonautCompanyId: z.string(),
  clientId: Uuid.nullable(),
  name: z.string(),
  siret: z.string().nullable(),
  city: z.string().nullable(),
  createdOn: z.string().nullable(),
  /** `siret` : même SIRET (ou même SIREN) ; `name` : même nom ; `similar` : nom qui ressemble (doublon possible). */
  kind: z.enum(['siret', 'name', 'similar']),
});
export type QuoteClientMatch = z.infer<typeof QuoteClientMatch>;

/** Ce que le registre public des entreprises (Recherche d'entreprises) dit du prospect. */
export const QuoteRegistryCompany = z.object({
  name: z.string(),
  siren: z.string(),
  siret: z.string().nullable(),
  street: z.string().nullable(),
  zipCode: z.string().nullable(),
  city: z.string().nullable(),
});
export type QuoteRegistryCompany = z.infer<typeof QuoteRegistryCompany>;

export const QuoteClientCheck = z.object({
  matches: z.array(QuoteClientMatch),
  registry: QuoteRegistryCompany.nullable(),
  /** Registre indisponible (panne, délai) : le pré-remplissage continue sans lui. */
  registryError: z.string().nullable(),
});
export type QuoteClientCheck = z.infer<typeof QuoteClientCheck>;

/** `POST /api/quote-drafts/:id/client-search` */
export const QuoteClientSearchResult = z.object({ draft: QuoteDraft, check: QuoteClientCheck });
export type QuoteClientSearchResult = z.infer<typeof QuoteClientSearchResult>;
